import { useState } from 'react';
import { Box, Flex, Image } from '@chakra-ui/react';
import Marquee from 'react-fast-marquee';
import SectionHeader from './SectionHeader';
import CtaButton from './CtaButton';
import CaseLightbox from './CaseLightbox';
import { CASES_SUBTITLE, caseImages } from '../data/caseImages';

/**
 * Home page teaser for the Cases page: an endless strip of case images and a CTA
 */
export default function CasesPreview() {

	// Index of the image shown in the lightbox, or null when closed
	const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

	return (
		<Flex
			id={"#cases"}
			width={"100%"}
			flexDirection={"column"}
			alignItems={"center"}
			gap={8}
			backgroundColor={"blue.100"}
			paddingY={{ base: 10, md: 16 }}
		>
			{/* Header */}
			<SectionHeader
				title={"Past Cases"}
				subtitle={CASES_SUBTITLE}
			/>

			{/* Case Images */}
			{caseImages.length > 0 && (
				<Marquee
					speed={40}
					// Stop scrolling while an image is open
					play={selectedIndex === null}
					pauseOnHover
					autoFill
					gradient
					gradientColor={"#252C40"}
					gradientWidth={80}
					// The marquee clips its overflow, so give hovered images and their shadows room
					// to grow, and cancel it out with negative margin so the section spacing is unchanged.
					// It only sets overflow-x, which makes it scrollable vertically; hide both.
					style={{ padding: "16px 0", margin: "-16px 0", overflow: "hidden" }}
				>
					{caseImages.map((src, index) => (
						<Box
							key={src}
							as={"button"}
							aria-label={`Enlarge past case ${index + 1}`}
							onClick={() => setSelectedIndex(index)}
							marginX={3}
							borderRadius={"md"}
							transition={"transform 0.2s ease"}
							_hover={{ transform: "scale(1.03)" }}
							_focusVisible={{ boxShadow: "0 0 0 3px #D0A144", outline: "none" }}
						>
							<Image
								src={src}
								alt={`Past case ${index + 1}`}
								height={{ base: "180px", md: "240px" }}
								width={"auto"}
								borderRadius={"md"}
								boxShadow={"lg"}
								draggable={false}
							/>
						</Box>
					))}
				</Marquee>
			)}

			{/* CTA */}
			<CtaButton to={"/cases"}>
				View Past Cases
			</CtaButton>

			{/* Enlarged Image */}
			<CaseLightbox
				images={caseImages}
				index={selectedIndex}
				onChange={setSelectedIndex}
				onClose={() => setSelectedIndex(null)}
			/>
		</Flex>
	);
}
